import React, { useCallback } from 'react';
import classnames from 'classnames';
import { Icon, SemanticICONS } from 'semantic-ui-react';
import styles from './button-toolbar.scss';
import { NavLink } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'react-feather';
interface IButtonToolbarItem {
  name: string;
  exact?: boolean;
  path?: string;
  itemClassName?: string;
  icon?: SemanticICONS | React.FC;
  isActive?: boolean;
  onClick?: (...args) => void;
  iconClassName?: string;
}

export interface IButtonToolbarProps {
  items: IButtonToolbarItem[];
  active?: string;
  onClick?: (...args) => void;
  onNextClick?: (...args) => void;
  onPrevClick?: (...args) => void;
  className?: string;
  itemClassName?: string;
  responsive?: boolean;
}

const ButtonItem = (item: IButtonToolbarItem) => {
  const {
    name,
    icon: CustomIcon,
    onClick = R.F,
    isActive,
    itemClassName,
    path,
    exact,
    iconClassName,
  } = item;

  const handleClick = useCallback(() => onClick(item), [onClick]);
  const activeClassName = classnames(
    isActive && styles.active,
    isActive && 'active',
  );

  const content = (
    <>
      {CustomIcon && (
        <div className={classnames(styles.itemIcon, iconClassName)}>
          {typeof CustomIcon === 'string' ? (
            <Icon name={CustomIcon} />
          ) : (
            <CustomIcon />
          )}
        </div>
      )}
      <div className={styles.itemName} title={name}>
        {name}
      </div>
    </>
  );

  if (path) {
    return (
      <NavLink
        id={`PolicyDetails_Tab_${name}`}
        className={classnames(styles.item, itemClassName)}
        activeClassName={classnames(styles.active, 'active')}
        onClick={handleClick}
        to={path}
        exact={exact}
      >
        {content}
      </NavLink>
    );
  }

  return (
    <div
      id={`PolicyDetails_Tab_${name}`}
      className={classnames(styles.item, itemClassName, activeClassName)}
      onClick={handleClick}
    >
      {content}
    </div>
  );
};

const getItem = (tabItems, name) => {
  const item = tabItems.find((tab) => tab.name === name);
  return item;
};

const MobileToolbar = ({ active, onPrev, onNext, items }) => {
  const { icon: CustomIcon, iconClassName } = getItem(items, active);
  return (
    <div className={styles.containerSmall}>
      <div className={styles.iconContainer}>
        <ChevronLeft onClick={onPrev} />
      </div>
      <div
        className={classnames(
          styles.itemName,
          CustomIcon && styles.lockedItemName,
        )}
      >
        {active}
        {CustomIcon && (
          <div className={classnames(styles.itemIcon, iconClassName)}>
            <CustomIcon />
          </div>
        )}
      </div>
      <div className={styles.iconContainer}>
        <ChevronRight onClick={onNext} />
      </div>
    </div>
  );
};

const ButtonToolbar = (props: IButtonToolbarProps) => {
  const {
    active,
    className,
    itemClassName,
    items,
    responsive,
    onClick,
    onNextClick,
    onPrevClick,
  } = props;

  const handlePrev = useCallback(() => {
    const activeIdx = R.findIndex(R.propEq('name', active), items);
    if (activeIdx >= 0) {
      const prevIdx = activeIdx - 1;
      // Loop to the last item if we click previous on the first item
      const item = prevIdx < 0 ? items[items.length - 1] : items[prevIdx];

      if (onPrevClick) {
        onPrevClick(item);
      } else {
        onClick(item);
      }
    }
  }, [active, items, onClick, onPrevClick]);

  const handleNext = useCallback(() => {
    const activeIdx = R.findIndex(R.propEq('name', active), items);
    if (activeIdx >= 0) {
      const nextIdx = activeIdx + 1;
      // Loop to the first item if we click next on the last item
      const item = nextIdx > items.length - 1 ? items[0] : items[nextIdx];

      if (onNextClick) {
        onNextClick(item);
      } else {
        onClick(item);
      }
    }
  }, [active, items, onClick, onNextClick]);

  return (
    <>
      <div className={classnames(styles.container, className)}>
        {items.map((item) => (
          <ButtonItem
            key={item.name}
            onClick={onClick}
            isActive={active === item.name}
            itemClassName={itemClassName}
            {...item}
          />
        ))}
      </div>
      {responsive && (
        <MobileToolbar
          active={active}
          onNext={handleNext}
          onPrev={handlePrev}
          items={items}
        />
      )}
    </>
  );
};

export default ButtonToolbar;
